import { Database, Goal, NotepadText, Settings2 } from 'lucide-react';
import type { ComponentType } from 'react';
import { STEP_KEYS, type StepKey } from './types';

export interface StepperEntry {
  key: StepKey;
  label: string;
  icon: ComponentType<{ size?: number | string; className?: string }>;
  heading: string;
  subHeading: string;
  subHeadingLive?: string;
}

export const STEPPER_CONFIG: StepperEntry[] = [
  {
    key: STEP_KEYS.CRITERIA,
    label: 'Criteria',
    icon: Goal,
    heading: 'Select criteria',
    subHeading: 'Choose the metrics you want to evaluate in this run.',
  },
  {
    key: STEP_KEYS.ENVIRONMENT,
    label: 'Environment',
    icon: NotepadText,
    heading: 'Select environment',
    subHeading: "Choose which environment's active build this run will evaluate.",
  },
  {
    key: STEP_KEYS.SCOPE,
    label: 'Scope',
    icon: Database,
    heading: 'Select scope',
    subHeading: 'Define the data scope for this run.',
    subHeadingLive: 'Select & configure the data scope to be analyzed for this run.',
  },
  {
    key: STEP_KEYS.CONFIGURE,
    label: 'Configure',
    icon: Settings2,
    heading: 'Configure',
    subHeading: 'Name your run and review your selections before proceeding.',
  },
];

export const RUN_NAME_LIMITS = {
  MIN: 2,
  MAX: 1000,
} as const;

export const LIVE_SAMPLE_RATE = {
  MIN: 1,
  MAX: 100,
} as const;

// Mirrors ENVIRONMENT_DROPDOWN_OPTIONS in aistudio's
// client/views/Calibration/constants/index.js.
export const ENVIRONMENT_OPTIONS = [
  {
    label: 'Staging',
    value: 'SANDBOX',
    description:
      'Analyze past conversations on Staging. Use this to evaluate agent quality before going live.',
  },
  {
    label: 'Production',
    value: 'LIVE',
    description:
      'Analyze live conversations on Production in real time. Use this to monitor live agent quality.',
  },
] as const;

// The topmost / production stage of the EP pipeline. Also the `releaseEnv` slug
// non-EP bots use for Production, so it doubles as the "is live" test for both.
export const LIVE_RELEASE_ENV = 'LIVE';

// Per-option description lines shown under each environment in the EP
// (DYNAMIC_ENV) dropdown, where env names are bot-specific and can't carry a
// hardcoded description of their own.
export const EP_ENV_DESCRIPTION = {
  LIVE: 'Evaluates live conversations on this build in real time.',
  NON_PROD: 'Replays historical user queries on this build and evaluates the new responses.',
} as const;

// Info tooltip on the Environment step (EP bots only).
export const EP_BUILD_CHANGE_WARNING =
  'Only the active build of each environment can be evaluated. To calibrate an inactive/rolled-back build, make it active first. Avoid changing the active build or editing its active objects while a run is in progress — mixed builds produce mixed results.';

export const METRIC_TYPES = {
  PREDEFINED: 'PREDEFINED',
  CUSTOM: 'CUSTOM',
} as const;

// Display metadata for a metric category (group header shown in the Criteria step).
export const METRIC_CATEGORY_META: Record<string, { groupName: string; description: string }> = {
  quality_metrics: {
    groupName: 'Quality metrics',
    description: 'Evaluate the overall quality of agent responses across your selected criteria.',
  },
};

export const getCategoryMeta = (category: string) =>
  METRIC_CATEGORY_META[category] ?? { groupName: category, description: '' };

export const CUSTOM_METRIC_FIELD_LIMITS = {
  NAME_MIN: 3,
  NAME_MAX: 60,
  DESCRIPTION_MAX: 600,
  ADDITIONAL_MAX: 600,
} as const;

// Copy for the Add-criterion flow (Figma uses "criterion/criteria" in UI copy).
export const ADD_CRITERION_COPY = {
  TITLE: 'Add criterion',
  SUBTITLE: "Write your own criterion. We'll validate it and turn it into a full rubric.",
  SUBMIT: 'Validate & Create',
  CLOSE_TOOLTIP: 'Closing this will discard the changes made',
  CANCEL_VALIDATION_TOOLTIP: 'Cancelling will interrupt metric creation',
} as const;

export interface AddCriterionFieldConfig {
  key: 'name' | 'description' | 'goodBehavior' | 'badBehavior' | 'skipCriteriaWhen';
  label: string;
  placeholder: string;
  type: 'text' | 'textarea';
  maxLength: number;
  showWordLimit?: boolean;
  liveValidate?: boolean;
  optional?: boolean;
  minRows?: number;
}

// Field config driving the Add-criterion form. Order here is the render order.
export const ADD_CRITERION_FIELDS: AddCriterionFieldConfig[] = [
  {
    key: 'name',
    label: 'Name',
    placeholder: 'Empathy and reassurance',
    type: 'text',
    maxLength: CUSTOM_METRIC_FIELD_LIMITS.NAME_MAX,
    showWordLimit: true,
    liveValidate: true,
  },
  {
    key: 'description',
    label: 'Description',
    placeholder:
      'Checks that the agent acknowledges customer frustration before resolving, and softens negative outcomes with reassuring language.',
    type: 'textarea',
    maxLength: CUSTOM_METRIC_FIELD_LIMITS.DESCRIPTION_MAX,
    minRows: 3,
    showWordLimit: true,
  },
  {
    key: 'goodBehavior',
    label: 'Example of good behavior',
    placeholder:
      "'I completely understand how frustrating a delayed refund is - let me look into this right away'.",
    type: 'textarea',
    maxLength: CUSTOM_METRIC_FIELD_LIMITS.ADDITIONAL_MAX,
    minRows: 2,
    optional: true,
  },
  {
    key: 'badBehavior',
    label: 'Example of bad behavior',
    placeholder:
      "Responding to 'I've been waiting three weeks!' with only 'Your refund is in processing status'.",
    type: 'textarea',
    maxLength: CUSTOM_METRIC_FIELD_LIMITS.ADDITIONAL_MAX,
    minRows: 2,
    optional: true,
  },
  {
    key: 'skipCriteriaWhen',
    label: 'Skip this criteria when',
    placeholder: 'The conversation contains no negative sentiment or complaint from the customer.',
    type: 'textarea',
    maxLength: CUSTOM_METRIC_FIELD_LIMITS.ADDITIONAL_MAX,
    minRows: 2,
    optional: true,
  },
];

export const CRITERION_NAME_ERROR = {
  REQUIRED: 'Enter a name for this criterion.',
  TOO_SHORT: `Name must be at least ${CUSTOM_METRIC_FIELD_LIMITS.NAME_MIN} characters.`,
  TOO_LONG: `Name cannot exceed ${CUSTOM_METRIC_FIELD_LIMITS.NAME_MAX} characters.`,
  TAKEN: 'This name is already in use. Enter a unique name.',
  INVALID_CHARS: 'Name can only contain letters, numbers, spaces, hyphens and underscores.',
} as const;

// Allowed characters for a metric name: letters, numbers, spaces, hyphen, underscore.
export const CRITERION_NAME_PATTERN = /^[a-zA-Z0-9 _-]+$/;

export const VALIDATION_STAGES = [
  'Validating for clarity, feasibility, and uniqueness',
  'Building evaluation prompt and scoring guidance',
  'Running a test evaluation and confirming metric is ready',
] as const;

export const VALIDATION_PROGRESS_TITLE = 'Preparing your criterion';

// Outcome banners (validation failure / system error) shown inside the modal.
export const METRIC_OUTCOME_BANNER = {
  VALIDATION_FAILED: { title: 'Validation failed' },
  SYSTEM_ERROR: { title: 'System error / Timeout' },
  INTERRUPTED: { title: 'Metric Creation Interrupted' },
} as const;

// Toasts shown after the modal closes on success.
export const METRIC_SUCCESS_TOAST = {
  title: 'Criteria added',
  description:
    'Your custom metric passed validation and is ready to use. It has been added to your quality metrics and selected for this run.',
} as const;
export const METRIC_SIMILAR_TOAST_TITLE = 'Criteria added, but similar metric found';

export const deleteInUseTooltip = (count: number) =>
  `Metric cannot be deleted as it is referenced in ${count} ${count === 1 ? 'run' : 'runs'}`;

// Shown on the disabled Add Criterion button once the bot's CALIBRATION_CONFIG
// customMetricLimit is reached. Copy matches aistudio's
// CUSTOM_METRIC_LIMIT_TOOLTIP (client/views/Calibration/constants/index.js).
export const CUSTOM_METRIC_LIMIT_TOOLTIP = 'Custom criteria limit reached';

export const runCountLabel = (count: number) =>
  count > 0 ? `${count} ${count === 1 ? 'run' : 'runs'}` : 'Not used';

export interface MetricDefinition {
  key: string;
  name: string;
  tooltip: string;
}

export interface MetricGroupDefinition {
  key: string;
  groupName: string;
  description: string;
  metrics: MetricDefinition[];
}

// Mirrors METRIC_GROUPS in aistudio's client/views/Calibration/constants/index.js.
// Keep in sync until calibration-config endpoint exposes these dynamically.
export const METRIC_GROUPS: MetricGroupDefinition[] = [
  {
    key: 'quality_metrics',
    groupName: 'Quality metrics',
    description:
      'Evaluate the overall quality of agent responses across accuracy, tone, and task completion.',
    metrics: [
      {
        key: 'playbook_adherence',
        name: 'Playbook Adherence',
        tooltip:
          'Evaluates whether the agent identifies the right playbook, triggers it at the right moment, and follows each step in order.',
      },
      {
        key: 'tool_integration_correctness',
        name: 'Tool Accuracy',
        tooltip:
          'Evaluates whether the agent calls the right tool with the right parameters at the right moment, assessed per turn.',
      },
      {
        key: 'relevance',
        name: 'Relevance',
        tooltip:
          "Evaluates whether each agent response directly addresses the customer's question without going off-topic or giving generic answers.",
      },
      {
        key: 'tone_brand_compliance',
        name: 'Tone & brand compliance',
        tooltip:
          'Evaluates whether the agent follows your Custom tone and Adjust response instructions.',
      },
    ],
  },
];
